import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Stack,
  Avatar,
  alpha,
  TextField,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useState, useMemo, useEffect } from 'react';
import type { VendorReviewDecision, VendorMatch } from '../../types/vendor';
import { VendorReviewDecisionType } from '../../types/vendor';
import type { VendorReviewWithMatches } from '../../types/vendor';
import InstructionsPanel from './InstructionsPanel';
import VendorList from './VendorList';
import {
  CancelWarningDialog,
  MapConfirmationDialog,
} from './ConfirmationDialogs';

interface VendorReviewModalProps {
  request: VendorReviewWithMatches | null;
  onClose: () => void;
  onSubmit: (decision: VendorReviewDecision) => Promise<void>;
  totalReviews?: number;
  currentReviewNumber?: number;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const VendorReviewModal: React.FC<VendorReviewModalProps> = ({
  request,
  onClose,
  onSubmit,
  totalReviews = 1,
  currentReviewNumber = 1,
}) => {
  const showProgress = totalReviews > 1;
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [decision, setDecision] = useState<VendorReviewDecisionType>(
    VendorReviewDecisionType.MapExisting,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [showMapConfirmation, setShowMapConfirmation] = useState(false);

  const matchingVendors: VendorMatch[] = request?.matchingVendors ?? [];

  useEffect(() => {
    if (request?.reviewId) {
      setSelectedVendor(null);
      setDecision(VendorReviewDecisionType.MapExisting);
      setSearchQuery('');
      setError(null);
      setShowCancelWarning(false);
      setShowMapConfirmation(false);
    }
  }, [request?.reviewId]);

  useEffect(() => {
    if (matchingVendors.length > 0 && !selectedVendor) {
      const highestMatch = matchingVendors.reduce((prev, current) =>
        (current.similarityScore ?? 0) > (prev.similarityScore ?? 0)
          ? current
          : prev,
      );
      if (highestMatch?.vendorId) {
        setSelectedVendor(highestMatch.vendorId);
      }
    }
  }, [matchingVendors, selectedVendor]);

  const filteredVendors = useMemo(() => {
    if (!searchQuery.trim()) return matchingVendors;
    const query = searchQuery.toLowerCase();
    return matchingVendors.filter(
      vendor =>
        vendor.vendorName?.toLowerCase().includes(query) ||
        vendor.companyName?.toLowerCase().includes(query) ||
        vendor.vendorGST?.toLowerCase().includes(query),
    );
  }, [matchingVendors, searchQuery]);

  if (!request) return null;

  const handleSubmit = async () => {
    if (decision === VendorReviewDecisionType.MapExisting && !selectedVendor) {
      setError('Please select a vendor to map to');
      return;
    }
    if (decision === VendorReviewDecisionType.MapExisting) {
      setShowMapConfirmation(true);
      return;
    }
    await executeSubmit();
  };

  const executeSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: decision,
        SelectedVendorId:
          decision === VendorReviewDecisionType.MapExisting && selectedVendor
            ? selectedVendor
            : undefined,
        NewVendorName:
          decision === VendorReviewDecisionType.CreateNew
            ? request.extractedVendorName
            : undefined,
      });
      // Don't call onClose() here! The hook's submitDecision handles navigation
      // If there are more reviews, it advances; if done, it clears state (which closes modal)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit vendor review';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMap = async () => {
    setShowMapConfirmation(false);
    await executeSubmit();
  };

  const handleDecisionChange = (newDecision: VendorReviewDecisionType) => {
    setDecision(newDecision);
    setError(null);
    if (newDecision === VendorReviewDecisionType.CreateNew) {
      setSelectedVendor(null);
    } else if (newDecision === VendorReviewDecisionType.MapExisting) {
      // Re-select the highest matching vendor when switching back to MapExisting
      if (matchingVendors.length > 0) {
        const highestMatch = matchingVendors.reduce((prev, current) =>
          (current.similarityScore ?? 0) > (prev.similarityScore ?? 0)
            ? current
            : prev,
        );
        if (highestMatch?.vendorId) {
          setSelectedVendor(highestMatch.vendorId);
        }
      }
    }
  };

  const handleCancelClick = () => {
    if (!loading) {
      setShowCancelWarning(true);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelWarning(false);
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: VendorReviewDecisionType.CreateNew,
        NewVendorName: request.extractedVendorName,
      });
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create vendor';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedVendor(null);
      setDecision(VendorReviewDecisionType.MapExisting);
      setError(null);
      setSearchQuery('');
      setShowCancelWarning(false);
      onClose();
    }
  };

  const handleSelectVendor = (vendorId: string) => {
    setSelectedVendor(vendorId);
    setError(null);
  };

  const selectedVendorName =
    matchingVendors.find(v => v.vendorId === selectedVendor)?.vendorName ??
    'Selected Vendor';

  return (
    <>
      <Dialog
        open={!!request}
        onClose={handleCancelClick}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            p: 2.5,
          }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: alpha('#fff', 0.2), width: 44, height: 44 }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h6" fontWeight="bold" fontSize="1.1rem">
                  Vendor Review Required
                </Typography>
                {showProgress && (
                  <Chip
                    label={`${currentReviewNumber} of ${totalReviews}`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#fff', 0.2),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {showProgress
                  ? `Review ${currentReviewNumber} of ${totalReviews} vendors`
                  : 'Match or create a new vendor entry'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex' }}>
          <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
            <InstructionsPanel />

            {/* Main Content */}
            <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
              {/* Extracted Vendor Info */}
              <Card
                variant="outlined"
                sx={{
                  mb: 2.5,
                  bgcolor: alpha('#1976d2', 0.04),
                  borderColor: alpha('#1976d2', 0.2),
                }}>
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textTransform="uppercase"
                    fontWeight="medium"
                    fontSize="0.65rem">
                    Extracted Vendor Name
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    fontWeight="bold"
                    sx={{
                      mt: 0.25,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                    {request.extractedVendorName}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {matchingVendors.length > 0 &&
                      matchingVendors[0].companyName && (
                        <Typography variant="caption" color="text.secondary">
                          Company:{' '}
                          <strong>{matchingVendors[0].companyName}</strong>
                        </Typography>
                      )}
                    {request.fileName && (
                      <Typography variant="caption" color="text.secondary">
                        File: <strong>{request.fileName}</strong>
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                  {error}
                </Alert>
              )}

              <RadioGroup
                value={decision}
                onChange={e =>
                  handleDecisionChange(
                    e.target.value as VendorReviewDecisionType,
                  )
                }>
                {/* Map to Existing Option */}
                <Card
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    borderColor:
                      decision === VendorReviewDecisionType.MapExisting
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === VendorReviewDecisionType.MapExisting ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(VendorReviewDecisionType.MapExisting)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value="MapExisting"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <LinkIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Map to existing vendor
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                    </CardContent>
                  </CardActionArea>

                  {decision === VendorReviewDecisionType.MapExisting &&
                    matchingVendors.length > 0 && (
                      <>
                        <Divider />
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                          <Autocomplete
                            options={matchingVendors}
                            getOptionLabel={(option: VendorMatch) =>
                              option.vendorName || ''
                            }
                            filterOptions={(options, { inputValue }) => {
                              const query = inputValue.toLowerCase();
                              return options.filter(
                                option =>
                                  option.vendorName
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.companyName
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.vendorGST
                                    ?.toLowerCase()
                                    .includes(query),
                              );
                            }}
                            inputValue={searchQuery}
                            onInputChange={(_, value) => setSearchQuery(value)}
                            onChange={(_, value) => {
                              if (value) {
                                setSelectedVendor(value.vendorId);
                                setSearchQuery(value.vendorName || '');
                                setError(null);
                              }
                            }}
                            autoHighlight
                            autoSelect
                            selectOnFocus
                            handleHomeEndKeys
                            openOnFocus
                            blurOnSelect
                            clearOnBlur={false}
                            renderOption={(props, option: VendorMatch) => (
                              <Box
                                component="li"
                                {...props}
                                key={option.vendorId}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  width="100%">
                                  <Box>
                                    <Typography
                                      fontSize="0.875rem"
                                      fontWeight="medium">
                                      {option.vendorName}
                                    </Typography>
                                    {option.companyName && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary">
                                        {option.companyName}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    label={`${option.similarityScore}%`}
                                    size="small"
                                    color={getMatchColor(
                                      option.similarityScore,
                                    )}
                                    sx={{
                                      fontWeight: 'bold',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                </Stack>
                              </Box>
                            )}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Search vendors... (Tab to select)"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary' }}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  mb: 1.5,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                  },
                                }}
                              />
                            )}
                            ListboxProps={{
                              sx: {
                                maxHeight: 200,
                                '& .MuiAutocomplete-option': { py: 1 },
                              },
                            }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{ mb: 1, display: 'block' }}
                            fontSize="0.65rem">
                            {filteredVendors.length} MATCHING VENDOR
                            {filteredVendors.length !== 1 ? 'S' : ''}
                          </Typography>

                          <VendorList
                            vendors={filteredVendors}
                            selectedVendor={selectedVendor}
                            onSelectVendor={handleSelectVendor}
                          />
                        </Box>
                      </>
                    )}
                </Card>

                {/* Create New Option */}
                <Card
                  variant="outlined"
                  sx={{
                    borderColor:
                      decision === VendorReviewDecisionType.CreateNew
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === VendorReviewDecisionType.CreateNew ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(VendorReviewDecisionType.CreateNew)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value="CreateNew"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <PersonAddIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Create new vendor
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                      {decision === VendorReviewDecisionType.CreateNew && (
                        <Box sx={{ ml: 4, mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.8rem"
                            sx={{
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}>
                            New vendor:{' '}
                            <Typography
                              component="span"
                              fontWeight="medium"
                              color="text.primary">
                              "{request.extractedVendorName}"
                            </Typography>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </RadioGroup>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50' }}>
          <Button
            onClick={handleCancelClick}
            disabled={loading}
            variant="outlined"
            size="small"
            color="warning"
            sx={{ borderRadius: 2, px: 2.5 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={
              loading ||
              (decision === VendorReviewDecisionType.MapExisting &&
                !selectedVendor)
            }
            sx={{ borderRadius: 2, px: 2.5, minWidth: 100 }}>
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelWarningDialog
        open={showCancelWarning}
        onClose={() => setShowCancelWarning(false)}
        onConfirm={handleConfirmCancel}
        loading={loading}
        vendorName={request.extractedVendorName}
      />

      <MapConfirmationDialog
        open={showMapConfirmation}
        onClose={() => setShowMapConfirmation(false)}
        onConfirm={handleConfirmMap}
        loading={loading}
        extractedVendorName={request.extractedVendorName}
        selectedVendorName={selectedVendorName}
      />
    </>
  );
};

export default VendorReviewModal;
